import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar,
  Alert as MuiAlert,
  IconButton,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Landscape as EnvironmentIcon,
  Sports as EquipmentIcon,
  DirectionsRun as StanceIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import api from '../../services/api';

const StudentEvaluationQuery = () => {
  const [formData, setFormData] = useState({
    studentName: '',
    snowfield: '',
    courseDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSearch = async () => {
    if (!formData.studentName.trim() || !formData.snowfield.trim() || !formData.courseDate) {
      showSnackbar('請填寫所有查詢條件', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/evaluations/query/', {
        params: {
          student_name: formData.studentName.trim(),
          snowfield: formData.snowfield.trim(),
          course_date: formData.courseDate
        }
      });
      
      setResults(response.data);
      if (response.data.length === 0) {
        showSnackbar('未找到符合條件的上課記錄', 'info');
      } else {
        showSnackbar(`找到 ${response.data.length} 筆上課記錄`, 'success');
      }
    } catch (error) {
      console.error('查詢上課記錄失敗:', error);
      showSnackbar('查詢失敗，請稍後再試', 'error');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // 檢測當前設備類型
  const isMobileDevice = () => {
    return window.innerWidth <= 768; // 768px 以下視為手機
  };

  const handleScreenshot = async (evaluation) => {
    try {
      const isMobile = isMobileDevice();
      
      // 找到原始卡片
      const originalCard = document.querySelector(`[data-evaluation-id="${evaluation.id}"]`);
      if (!originalCard) {
        showSnackbar('找不到評量記錄卡片', 'error');
        return;
      }
      
      // 暫時隱藏按鈕區域並處理焦點
      const buttonContainer = originalCard.querySelector('[data-button-container]');
      const originalDisplay = buttonContainer ? buttonContainer.style.display : '';
      const originalAriaHidden = buttonContainer ? buttonContainer.getAttribute('aria-hidden') : null;
      
      if (buttonContainer) {
        // 移除焦點並隱藏
        const focusedButton = buttonContainer.querySelector(':focus');
        if (focusedButton) {
          focusedButton.blur();
        }
        buttonContainer.style.display = 'none';
        buttonContainer.setAttribute('aria-hidden', 'true');
      }
      
      // 截圖
      const canvas = await html2canvas(originalCard, {
        scale: isMobile ? 1.5 : 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        width: isMobile ? 375 : undefined,
        height: undefined
      });
      
      // 恢復按鈕顯示和aria-hidden屬性
      if (buttonContainer) {
        buttonContainer.style.display = originalDisplay;
        if (originalAriaHidden !== null) {
          buttonContainer.setAttribute('aria-hidden', originalAriaHidden);
        } else {
          buttonContainer.removeAttribute('aria-hidden');
        }
      }
      
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `上課記錄_${evaluation?.student_name}_${evaluation?.course_date}.png`;
      link.href = image;
      link.click();
      showSnackbar('截圖下載成功', 'success');
    } catch (error) {
      console.error('截圖失敗:', error);
      showSnackbar('截圖失敗，請稍後再試', 'error');
    }
  };


  const formatEvaluationData = (evaluation) => {
    const data = {
      '環境': evaluation.environment,
      '裝備': evaluation.equipment,
      '站姿': evaluation.stance,
      '問題-階段': evaluation.problem_phase,
      '問題-表現': evaluation.problem_performance,
      '問題-動作': evaluation.problem_movement,
      '改善-練習': evaluation.improvement_exercise,
      '改善-動作': evaluation.improvement_movement,
      '改善-動作細節': evaluation.improvement_movement_detail,
      '評語': evaluation.comments
    };

    return Object.entries(data).filter(([key, value]) => value && value.toString().trim() !== '');
  };

  const renderEvaluationCard = (evaluation, index) => (
    <Card 
      key={evaluation.id || index} 
      sx={{ mb: 2, boxShadow: 2 }}
      data-evaluation-id={evaluation.id}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              {evaluation.student_name || '未知學生'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip
                icon={<LocationIcon />}
                label={evaluation.course_location || '未知雪場'}
                size="medium"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.9rem' }}
              />
              <Chip
                icon={<CalendarIcon />}
                label={evaluation.course_date || '未知日期'}
                size="medium"
                color="secondary"
                variant="outlined"
                sx={{ fontSize: '0.9rem' }}
              />
            </Box>
          </Box>
          <Box data-button-container>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleScreenshot(evaluation)}
              size="medium"
              sx={{ fontSize: '0.9rem' }}
              data-screenshot-button
            >
              截圖下載
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 1 }} />
        
        {/* 環境區域 */}
        {evaluation.environment && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              mb: 2, 
              borderRadius: 2, 
              bgcolor: 'grey.50',
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EnvironmentIcon sx={{ fontSize: '1.5rem', color: 'primary.main', mr: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                環境
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
              {evaluation.environment}
            </Typography>
          </Paper>
        )}

        {/* 裝備區域 */}
        {evaluation.equipment && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              mb: 2, 
              borderRadius: 2, 
              bgcolor: 'grey.50',
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EquipmentIcon sx={{ fontSize: '1.5rem', color: 'secondary.main', mr: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                裝備
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
              {evaluation.equipment}
            </Typography>
          </Paper>
        )}

        {/* 站姿區域 */}
        {evaluation.stance && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              mb: 2, 
              borderRadius: 2, 
              bgcolor: 'grey.50',
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <StanceIcon sx={{ fontSize: '1.5rem', color: 'success.main', mr: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                站姿
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
              {evaluation.stance}
            </Typography>
          </Paper>
        )}

        {/* 問題分析和改善建議分區域顯示 */}
        <Grid container spacing={3}>
          {/* 問題分析區域 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 2 }}>
                問題分析
              </Typography>
              <List dense>
                {evaluation.problem_phase && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AssessmentIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="階段" 
                      secondary={evaluation.problem_phase}
                      primaryTypographyProps={{ variant: 'body1', fontWeight: 'bold', fontSize: '1rem' }}
                      secondaryTypographyProps={{ variant: 'body1', fontSize: '0.95rem' }}
                    />
                  </ListItem>
                )}
                {evaluation.problem_performance && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AssessmentIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="表現" 
                      secondary={evaluation.problem_performance}
                      primaryTypographyProps={{ variant: 'body1', fontWeight: 'bold', fontSize: '1rem' }}
                      secondaryTypographyProps={{ variant: 'body1', fontSize: '0.95rem' }}
                    />
                  </ListItem>
                )}
                {evaluation.problem_movement && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AssessmentIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="動作" 
                      secondary={evaluation.problem_movement}
                      primaryTypographyProps={{ variant: 'body1', fontWeight: 'bold', fontSize: '1rem' }}
                      secondaryTypographyProps={{ variant: 'body1', fontSize: '0.95rem' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
          
          {/* 改善建議區域 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 2 }}>
                改善建議
              </Typography>
              <List dense>
                {evaluation.improvement_exercise && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AssessmentIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="練習" 
                      secondary={evaluation.improvement_exercise}
                      primaryTypographyProps={{ variant: 'body1', fontWeight: 'bold', fontSize: '1rem' }}
                      secondaryTypographyProps={{ variant: 'body1', fontSize: '0.95rem' }}
                    />
                  </ListItem>
                )}
                {evaluation.improvement_movement && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AssessmentIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="動作" 
                      secondary={evaluation.improvement_movement}
                      primaryTypographyProps={{ variant: 'body1', fontWeight: 'bold', fontSize: '1rem' }}
                      secondaryTypographyProps={{ variant: 'body1', fontSize: '0.95rem' }}
                    />
                  </ListItem>
                )}
                {evaluation.improvement_movement_detail && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <AssessmentIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="細節" 
                      secondary={
                        Array.isArray(evaluation.improvement_movement_detail) 
                          ? evaluation.improvement_movement_detail.map((detail, index) => (
                              <Box key={index} component="div" sx={{ mb: 0.5 }}>
                                • {detail}
                              </Box>
                            ))
                          : evaluation.improvement_movement_detail
                      }
                      primaryTypographyProps={{ variant: 'body1', fontWeight: 'bold', fontSize: '1rem' }}
                      secondaryTypographyProps={{ variant: 'body1', fontSize: '0.95rem' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* 其他評量資訊 */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {formatEvaluationData(evaluation)
            .filter(([key]) => !['環境', '裝備', '站姿', '問題-階段', '問題-表現', '問題-動作', '改善-練習', '改善-動作', '改善-動作細節'].includes(key))
            .map(([key, value]) => (
            <Grid item xs={12} key={key}>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                {key}
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, boxShadow: 2 }}>
                <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
                  {Array.isArray(value) ? value.join('、') : value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4
    }}>
      <Container maxWidth="lg">
        {/* 返回首頁按鈕 */}
        <Fade in timeout={500}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
            <Tooltip title="返回首頁 ASA-滑雪裝忙協會">
              <IconButton
                href="/"
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                  }
                }}
              >
                <HomeIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Fade>

        {/* 主要內容區域 */}
        <Paper 
          elevation={8}
          sx={{ 
            p: 4,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.3)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              上課紀錄查詢
            </Typography>
            <Typography variant="body1" color="textSecondary">
              請輸入您的姓名、上課雪場和上課日期來查詢上課記錄
            </Typography>
          </Box>

      {/* 查詢表單 */}
      <Card sx={{ mb: 4, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            查詢條件
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="學生姓名"
                value={formData.studentName}
                onChange={handleInputChange('studentName')}
                placeholder="請輸入您的姓名"
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="上課雪場"
                value={formData.snowfield}
                onChange={handleInputChange('snowfield')}
                placeholder="請輸入雪場名稱"
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="上課日期"
                type="date"
                value={formData.courseDate}
                onChange={handleInputChange('courseDate')}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0], // 限制不能選擇未來日期
                  min: '2020-01-01' // 設定最小日期
                }}
                required
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? <CircularProgress size={24} /> : '查詢上課記錄'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 查詢結果 */}
      {results.length > 0 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            查詢結果 ({results.length} 筆記錄)
          </Typography>
          {results.map((evaluation, index) => renderEvaluationCard(evaluation, index))}
        </Box>
      )}


      {/* 提示訊息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
        </Paper>
      </Container>
    </Box>
  );
};

export default StudentEvaluationQuery;
